<template>
  <div class="tcla-article-report">
    <div class="tcla-panel tcla-panel--toolbar">
      <div class="tcla-toolbar__info">
        <strong>{{ articles.length }}</strong> مقاله تحلیل شد — از ضعیف‌ترین به بهترین مرتب شده‌اند
      </div>
      <div class="tcla-toolbar__actions">
        <button class="tcla-btn" @click="$emit('copy-json')">📋 کپی JSON</button>
        <button class="tcla-btn tcla-btn--primary" @click="$emit('download-json')">⬇️ دانلود JSON</button>
      </div>
    </div>

    <div v-if="articles.length === 0" class="tcla-empty">داده‌ای برای نمایش نیست</div>

    <div v-for="article in articles" :key="article.url" class="tcla-article-card">
      <div class="tcla-article-card__head" @click="toggle(article.url)">
        <div class="tcla-article-card__title-row">
          <span class="tcla-expand-icon">{{ expanded[article.url] ? '▼' : '◀' }}</span>
          <strong>{{ article.title }}</strong>
          <span v-if="article.category" class="tcla-tag tcla-tag--blue">{{ article.category }}</span>
          <span v-else class="tcla-tag tcla-tag--warn">بدون دسته‌بندی</span>
        </div>
        <span class="tcla-score-pill" :class="scoreClass(article.scores.seoScore)">{{ article.scores.seoScore }}</span>
      </div>

      <div v-show="expanded[article.url]" class="tcla-article-card__body">
        <!-- Score breakdown -->
        <div class="tcla-section">
          <div class="tcla-section__title">📊 ریز امتیازها</div>
          <div class="tcla-score-grid">
            <div class="tcla-score-item">
              <span>کیفیت محتوا</span>
              <span class="tcla-score-pill" :class="scoreClass(article.scores.contentQualityScore)">{{ article.scores.contentQualityScore }}</span>
            </div>
            <div class="tcla-score-item">
              <span>اقتدار</span>
              <span class="tcla-score-pill" :class="scoreClass(article.scores.authorityScore)">{{ article.scores.authorityScore }}</span>
            </div>
            <div class="tcla-score-item">
              <span>لینک‌دهی داخلی</span>
              <span class="tcla-score-pill" :class="scoreClass(article.scores.internalLinkingScore)">{{ article.scores.internalLinkingScore }}</span>
            </div>
            <div class="tcla-score-item">
              <span>تنوع انکر</span>
              <span class="tcla-score-pill" :class="scoreClass(article.scores.anchorScore)">{{ article.scores.anchorScore }}</span>
            </div>
          </div>
        </div>

        <!-- Content diagnostics checklist -->
        <div class="tcla-section">
          <div class="tcla-section__title">📝 وضعیت محتوا</div>
          <div class="tcla-checklist">
            <div class="tcla-check-item" :class="article.contentDiagnostics.wordCount >= 600 ? 'ok' : 'bad'">
              {{ article.contentDiagnostics.wordCount >= 600 ? '✅' : '⚠️' }} تعداد کلمات: {{ article.contentDiagnostics.wordCount }}
            </div>
            <div class="tcla-check-item" :class="article.contentDiagnostics.headingCount >= 3 ? 'ok' : 'bad'">
              {{ article.contentDiagnostics.headingCount >= 3 ? '✅' : '⚠️' }} تعداد تیتر: {{ article.contentDiagnostics.headingCount }}
            </div>
            <div class="tcla-check-item" :class="article.contentDiagnostics.imageCount > 0 ? 'ok' : 'bad'">
              {{ article.contentDiagnostics.imageCount > 0 ? '✅' : '⚠️' }} تعداد عکس: {{ article.contentDiagnostics.imageCount }}
              <span v-if="article.contentDiagnostics.imagesWithoutAlt > 0" class="tcla-inline-warn">({{ article.contentDiagnostics.imagesWithoutAlt }} بدون alt)</span>
            </div>
            <div class="tcla-check-item" :class="article.contentDiagnostics.tableCount > 0 ? 'ok' : 'neutral'">
              {{ article.contentDiagnostics.tableCount > 0 ? '✅' : '➖' }} جدول: {{ article.contentDiagnostics.tableCount > 0 ? 'دارد' : 'ندارد' }}
            </div>
            <div class="tcla-check-item" :class="article.contentDiagnostics.keywordInTitle ? 'ok' : 'bad'">
              {{ article.contentDiagnostics.keywordInTitle ? '✅' : '⚠️' }} کلمه کلیدی در عنوان
            </div>
            <div class="tcla-check-item" :class="article.contentDiagnostics.keywordInHeadings ? 'ok' : 'bad'">
              {{ article.contentDiagnostics.keywordInHeadings ? '✅' : '⚠️' }} کلمه کلیدی در تیترها
            </div>
          </div>
        </div>

        <!-- Link status -->
        <div class="tcla-section">
          <div class="tcla-section__title">🔗 وضعیت لینک‌دهی</div>
          <div class="tcla-hub-card__meta">
            <span>ورودی: {{ article.linkStatus.incomingCount }}</span>
            <span>·</span>
            <span>خروجی: {{ article.linkStatus.outgoingCount }}</span>
            <span v-if="article.linkStatus.isOrphan" class="tcla-tag tcla-tag--err">یتیم</span>
            <span v-if="article.linkStatus.isDeadEnd" class="tcla-tag tcla-tag--warn">بن‌بست</span>
            <span v-if="article.linkStatus.isHub" class="tcla-tag tcla-tag--blue">Hub</span>
          </div>
        </div>

        <!-- Link suggestions -->
        <div v-if="article.linkSuggestions.length" class="tcla-section">
          <div class="tcla-section__title">💡 پیشنهاد لینک‌سازی ({{ article.linkSuggestions.length }})</div>
          <div v-for="(link, i) in article.linkSuggestions" :key="i" class="tcla-suggestion-line">
            <span class="tcla-arrow">←</span> {{ link.targetTitle }}
            <div class="tcla-suggestion-line__reason">{{ link.reason }}</div>
          </div>
        </div>

        <!-- Category suggestion -->
        <div v-if="article.categorySuggestion" class="tcla-section">
          <div class="tcla-section__title">🏷️ پیشنهاد دسته‌بندی</div>
          <div class="tcla-cat-suggestion">
            این مقاله دسته‌بندی ندارد. پیشنهاد: <strong>{{ article.categorySuggestion.category }}</strong>
            (تطابق {{ article.categorySuggestion.score }}٪)
          </div>
        </div>

        <!-- Actions -->
        <div v-if="article.actions.length" class="tcla-section">
          <div class="tcla-section__title">✅ اقدامات پیشنهادی</div>
          <div v-for="(action, i) in article.actions" :key="i" class="tcla-action-line">
            {{ action.icon }} {{ action.text }}
          </div>
        </div>

        <div class="tcla-article-card__footer">
          <button class="tcla-btn" @click="$emit('edit-blog', article.source)">ویرایش این مقاله</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TCLAArticleReport',
  props: {
    articles: { type: Array, default: () => [] }
  },
  data() {
    return { expanded: {} };
  },
  methods: {
    toggle(url) {
      this.$set ? this.$set(this.expanded, url, !this.expanded[url]) : (this.expanded[url] = !this.expanded[url]);
      this.expanded = { ...this.expanded };
    },
    scoreClass(score) {
      if (score >= 70) return 'tcla-score--good';
      if (score >= 40) return 'tcla-score--mid';
      return 'tcla-score--bad';
    }
  }
};
</script>

<style scoped>
.tcla-panel {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  padding: 20px;
  margin-bottom: 16px;
}

.tcla-panel--toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.tcla-toolbar__info { font-size: 0.85rem; color: #4b5563; }
.tcla-toolbar__actions { display: flex; gap: 8px; }

.tcla-empty { text-align: center; padding: 20px; color: #9ca3af; }

.tcla-article-card {
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  margin-bottom: 12px;
  overflow: hidden;
  background: #fff;
}

.tcla-article-card__head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px;
  background: #f9fafb;
  cursor: pointer;
  gap: 12px;
}
.tcla-article-card__head:hover { background: #f3f4f6; }

.tcla-article-card__title-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  min-width: 0;
}

.tcla-expand-icon { font-size: 0.7rem; color: #9ca3af; flex-shrink: 0; }

.tcla-article-card__body {
  padding: 16px;
  border-top: 1px solid #e5e7eb;
}

.tcla-section { margin-bottom: 16px; }
.tcla-section:last-child { margin-bottom: 0; }
.tcla-section__title { font-size: 0.8rem; font-weight: 700; color: #4b5563; margin-bottom: 8px; }

.tcla-score-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
}
.tcla-score-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: center;
  background: #f9fafb;
  border-radius: 0.375rem;
  padding: 8px;
  font-size: 0.72rem;
  color: #6b7280;
}

.tcla-checklist { display: flex; flex-direction: column; gap: 6px; }
.tcla-check-item { font-size: 0.82rem; padding: 4px 0; }
.tcla-check-item.bad { color: #d97706; }
.tcla-check-item.ok { color: #16a34a; }
.tcla-check-item.neutral { color: #9ca3af; }
.tcla-inline-warn { color: #ef4444; font-size: 0.75rem; }

.tcla-suggestion-line, .tcla-action-line {
  font-size: 0.8rem;
  padding: 6px 0;
  color: #374151;
  border-bottom: 1px dashed #e5e7eb;
}
.tcla-suggestion-line:last-child, .tcla-action-line:last-child { border-bottom: none; }
.tcla-suggestion-line__reason { font-size: 0.72rem; color: #9ca3af; margin-top: 2px; }
.tcla-arrow { color: #9ca3af; }

.tcla-cat-suggestion {
  font-size: 0.82rem;
  background: #fef3c7;
  color: #92400e;
  padding: 10px;
  border-radius: 0.375rem;
}

.tcla-hub-card__meta {
  font-size: 0.8rem;
  color: #6b7280;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
}

.tcla-article-card__footer {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: flex-end;
}

.tcla-tag {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 600;
}
.tcla-tag--blue { background: #dbeafe; color: #3b82f6; }
.tcla-tag--warn { background: #fef3c7; color: #d97706; }
.tcla-tag--err { background: #fee2e2; color: #ef4444; }

.tcla-score-pill {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 100px;
  font-weight: 700;
  font-size: 0.75rem;
}
.tcla-score--good { background: #d1fae5; color: #16a34a; }
.tcla-score--mid { background: #fef3c7; color: #d97706; }
.tcla-score--bad { background: #fee2e2; color: #ef4444; }

.tcla-btn {
  padding: 7px 14px;
  background: #f3f4f6;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.8rem;
  cursor: pointer;
}
.tcla-btn:hover { background: #e5e7eb; }
.tcla-btn--primary { background: #3b82f6; color: #fff; border-color: #3b82f6; }
.tcla-btn--primary:hover { background: #2563eb; }
</style>
